import { v5 as uuidv5 } from 'uuid';
export const TABELAS = [
    'perfil',
    'pessoa',
    'visita',
    'estudo',
    'sessao',
    'mes',
    'territorio',
    'casa',
];
/**
 * O `user_id` que a escrita local carimba numa linha nova quando **não há
 * conta nenhuma** (ADR-0005). A spec é explícita em que o app funciona
 * inteiro sem conta, mas `user_id` é obrigatório no schema de toda tabela:
 * alguém tem de ser o dono, e sem sessão esse alguém é esta sentinela.
 *
 * Um UUID fixo e reconhecível, não um sorteado por aparelho: achar as linhas
 * escritas sem conta depois é um `where('user_id').equals(CONTA_LOCAL)` — e é
 * exatamente isso que a **adoção** da troca de conta faz (ver `motor.ts`).
 *
 * **Distinto de propósito** do `00000000-0000-4000-8000-000000000000` que o
 * dublê do PostgREST usa como preenchimento de PK. Dois sentinelas iguais em
 * camadas diferentes é o tipo de coincidência que faz um teste passar pelo
 * motivo errado.
 *
 * Mora aqui, e não na camada do app, porque quem precisa reconhecê-la são os
 * dois lados: a escrita local (`apps/sheafyield/src/dados/dono.ts`, que a
 * reexporta) e a purga de troca de conta, dentro do motor.
 */
export const CONTA_LOCAL = '10ca1000-0000-4000-8000-000000000000';
export const CURSOR_ZERO = '1970-01-01T00:00:00.000Z';
// O `now()` do carimbo é o do início da transação; uma transação lenta pode
// cometer com carimbo mais velho que o cursor já gravado. A janela cobre a
// transação mais longa que o servidor aceita: o `statement_timeout` do papel
// `authenticated` no Supabase é 8 s, e 15 s dá folga para o commit em cima
// disso. Reaplicar é no-op pelo LWW; perder é definitivo.
export const SOBREPOSICAO_DO_PULL_MS = 15000;
export const LOTE_DO_PUSH = 200;
// Desistir é preciso — sem teto, uma linha que o servidor nunca aceita
// repete com backoff para sempre, `mortas` fica em zero e Ajustes nunca
// mostra "não subiram" nem tem o que reenfileirar: a falha some da vista do
// usuário. Desistir não perde dado nenhum, porque `dead_letter` é justamente
// a lista visível que o botão "Reenfileirar" devolve à fila.
//
// **São duas desistências, porque são duas falhas diferentes.**
//
// O 403 é o único status que acusa a **linha**: a RLS a recusou
// (`with check (auth.uid() = user_id)`), e mandar a mesma linha de novo não
// muda a resposta. Aí contar voltas é contar a coisa certa — dez recusas e
// a entrada desiste.
export const TETO_DE_TENTATIVAS = 10;
// Todo o resto acusa o **transporte**, não a linha: 5xx do servidor, e
// também o 401/408/429 e o fetch quebrado que `transporteSupabase` mapeia
// para 500. Contar voltas aqui contaria a coisa errada, porque a volta não
// custa tempo. O backoff antes da décima volta soma cerca de 8min31s (1 s
// dobrando até o teto de 5 min), e **cada escrita local agenda um ciclo 2 s
// depois**: durante uma queda de rede, dez pausas do cronômetro drenariam a
// fila inteira para `dead_letter` em torno de dois minutos — com o aparelho
// offline, o servidor intacto e nada de errado com os dados. Por isso a
// desistência por transporte é por **tempo decorrido**: 24 h contadas da
// primeira falha — relógio de parede, não falha ininterrupta —, e o carimbo
// se apaga assim que a linha sobe.
export const JANELA_DE_FALHA_MS = 24 * 60 * 60 * 1000;
export const PAGINA_DO_PULL = 1000;
// Carimbos são sempre toISOString(): comparação lexicográfica é comparação
// temporal. No empate a remota NÃO entra (é eco); desempate é do servidor.
export function deveAplicarRemota(localUpdatedAt, remotoUpdatedAt) {
    if (localUpdatedAt === null || localUpdatedAt === undefined)
        return true;
    return remotoUpdatedAt > localUpdatedAt;
}
// O PostgREST devolve "2026-09-05 10:00:00.123456+00"; o LWW compara strings.
export function normalizarIso(valor) {
    if (typeof valor !== 'string' || valor === '')
        return '';
    const data = new Date(valor);
    return Number.isNaN(data.getTime()) ? '' : data.toISOString();
}
// Relógio do aparelho atrasado não pode produzir um carimbo que o lww_guard
// rejeite em silêncio: toda escrita avança pelo menos 1 ms sobre a anterior.
export function carimboSeguinte(agora, anterior) {
    if (!anterior)
        return agora;
    const minimo = new Date(new Date(anterior).getTime() + 1).toISOString();
    return agora > minimo ? agora : minimo;
}
export function dedupFila(entradas) {
    const porChave = new Map();
    for (const e of entradas)
        porChave.set(`${e.tabela}\0${e.id}`, e);
    return { finais: [...porChave.values()].sort((a, b) => a.seq - b.seq) };
}
export function emLotes(itens, tamanho) {
    const lotes = [];
    for (let i = 0; i < itens.length; i += tamanho)
        lotes.push(itens.slice(i, i + tamanho));
    return lotes;
}
// perfil e mes têm unique secundário no servidor; o upsert do PostgREST só
// resolve conflito pela PK, então a PK tem de ser a chave natural.
export function idDoPerfil(userId) {
    return userId;
}
export function idDoMes(userId, ano, mes) {
    return uuidv5(`${String(ano)}-${String(mes)}`, userId);
}
//# sourceMappingURL=protocolo.js.map